import { NetworkChainIds } from '@dapp/multi-lib/network';
import { useDebounce } from 'use-debounce';
import {
  useConnection,
  useReadContract,
  useWriteContract,
  useSimulateContract,
  usePublicClient,
} from 'wagmi';
import IERC20 from '@uniswap/v2-core/build/IERC20.json';
import { QueryOptions } from 'utils/useQueryUtils';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { ERC20_APPROVE_ABI } from 'utils/erc20Approval';

interface UseApproveParams extends QueryOptions {
  tokenAddress: `0x${string}`;
  spenderAddress: `0x${string}`;
  from?: `0x${string}`;
  tokenAmount: string;
  chainId: number;
}

export const useApprove = (params: UseApproveParams) => {
  const { tokenAddress, spenderAddress, tokenAmount, chainId, from, query } =
    params;
  const account = useConnection();
  const approvalAccount = from || account.address;

  const useQueryOptions = {
    queryKey: queryKeys.approveAllowance({
      tokenAddress,
      spenderAddress,
      account: from || account.address || '0x0',
      tokenAmount,
      chainId,
    }),
    ...query,
  };

  const prepare = useSimulateContract({
    address: tokenAddress,
    chainId,
    abi: ERC20_APPROVE_ABI,
    functionName: 'approve',
    args: [spenderAddress, BigInt(tokenAmount)],
    account: approvalAccount,
    query: useQueryOptions,
  });

  const publicClient = usePublicClient({ chainId });
  const contract = useWriteContract();

  const prepareFresh = async (amount = tokenAmount) => {
    if (!publicClient || !approvalAccount) {
      throw new Error('Approval simulation client is not available');
    }

    return publicClient.simulateContract({
      account: approvalAccount,
      address: tokenAddress,
      abi: ERC20_APPROVE_ABI,
      functionName: 'approve',
      args: [spenderAddress, BigInt(amount)],
    });
  };

  return { prepare, prepareFresh, contract };
};

interface UseAllowanceParams extends QueryOptions {
  chainId: NetworkChainIds;
  tokenAddress: `0x${string}`;
  addressOwner: `0x${string}`;
  addressSpender: `0x${string}`;
}

export const useAllowance = (params: UseAllowanceParams) => {
  const { tokenAddress, chainId, addressOwner, addressSpender } = params;
  const [debouncedAddressOwner] = useDebounce(addressOwner, 500);
  const [debouncedAddressSpender] = useDebounce(addressSpender, 500);

  const useQueryOptions = {
    queryKey: queryKeys.allowance({
      tokenAddress,
      chainId,
      addressOwner,
      addressSpender,
    }),
    ...params.query,
  };

  return useReadContract({
    address: tokenAddress,
    chainId,
    abi: IERC20.abi,
    functionName: 'allowance',
    args: [debouncedAddressOwner, debouncedAddressSpender],
    query: useQueryOptions,
  });
};
